import {
  LitElement,
  html,
  css,
  ifDefined,
  classMap,
  ref,
  createRef,
} from '@hh/global.js'
class HHCustomSelect extends LitElement {
  static properties = {
    options: {
      type: Object,
    },
    name: {
      type: String,
    },
    active: {
      type: String,
      reflect: true,
    },
    lang: {
      type: Boolean,
    },
    _selected: {
      state: true,
    },
    _active: {
      state: true,
    },
    _showAbove: {
      state: true,
    },
    _currentOption: {
      state: true,
    },
  }

  $list = createRef()

  constructor() {
    super()

    this.handleBodyClick = this.handleBodyClick.bind(this)

    this.uuid = Math.random() * (1000000 - 1) + 1
  }

  connectedCallback() {
    super.connectedCallback()
    this._selected = this.active
    document.body.addEventListener('click', this.handleBodyClick.bind(this))
    document.addEventListener('focusin', this.handleDocumentFocusIn.bind(this))
    this.addEventListener('keydown', this.handleKeydown)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    document.body.addEventListener('click', this.handleBodyClick)
    document.removeEventListener('focusin', this.handleFocusIn)
    this.removeEventListener('keydown', this.handleKeydown)
  }

  updated(changedProperties) {
    if (changedProperties.has('_selected')) {
      this.updateCurrentSelection()
    }
    if (changedProperties.has('_active') && this._active) {
      this._setCurrentOption(this._currentOption || this._getFirstOption())
    }
  }

  handleBodyClick(e) {
    const path = e.path || (e.composedPath && e.composedPath())
    if (
      path.some((node) => {
        return node === this
      })
    ) {
      return true
    }

    this._active && this.toggle()
  }

  toggle(e = false) {
    if (e && e.type === 'keyup' && !~['ESCAPE'].indexOf(e.code.toUpperCase())) {
      return true
    }

    this._showAbove =
      window.innerHeight - this.$list?.value.getBoundingClientRect().bottom <
      this.$list?.value.scrollHeight

    this._active = !this._active
  }

  selectValue(e, value) {
    e.preventDefault()
    this._selected = value
    this._setCurrentOption(e.target)
    ;(this.querySelector('[slot="input"]') || {}).value = value
    this.dispatchEvent(
      new CustomEvent('change', {
        bubbles: true,
        composed: true,
        detail: {
          value,
        },
      })
    )
    this.toggle()
  }

  setValueExternally(value) {
    this._selected = value
    ;(this.querySelector('[slot="input"]') || {}).value = value
  }

  updateCurrentSelection() {
    if (!this.button || this._selected === '') return
    this.button.textContent = this._selected
  }

  addOption(label, value) {
    const option = { label, value }
    this.options = [...this.options, option]
  }

  handleKeydown(e) {
    if (e.key.toUpperCase() === 'ESCAPE') {
      e.preventDefault()
      e.stopPropagation()
      this._active = false
      this.button.focus({ preventScroll: true })
    }

    if (e.code.toUpperCase() == 'SPACE' || e.key === ' ') {
      if (this._active) {
        e.preventDefault()
        e.stopPropagation()
      }

      this.toggle(e)
      return
    }

    if (['ArrowUp', 'ArrowDown'].includes(e.key)) {
      const allOptions = this._getAllOptions()
      const currentIndex = allOptions.indexOf(this._currentOption)
      let newIndex = Math.max(0, currentIndex)

      // Prevent scrolling
      e.preventDefault()

      if (!this._active) {
        this.toggle(e)
        if (this._currentOption) {
          this._currentOption.focus({ preventScroll: true })
          return
        }
      }

      if (e.key === 'ArrowDown') {
        newIndex = currentIndex + 1
        //  Only if we want looping
        // if (newIndex > allOptions.length - 1) newIndex = 0
      } else if (e.key === 'ArrowUp') {
        newIndex = currentIndex - 1
        //  Only if we want looping
        // if (newIndex < 0) newIndex = allOptions.length - 1
      }

      this._setCurrentOption(allOptions[newIndex])
    }
  }

  _getAllOptions() {
    return [...this.renderRoot?.querySelectorAll('.link')]
  }

  _getFirstOption() {
    return this.renderRoot?.querySelector('.link')
  }

  _setCurrentOption(option) {
    const allOptions = this._getAllOptions()

    // Clear selection
    allOptions.forEach((el) => {
      el.current = false
      el.tabIndex = -1
    })

    // Select the target option
    if (option) {
      this._currentOption = option
      this._selected = option.textContent
      option.current = true
      option.tabIndex = 0
      option.focus()
    }
  }

  handleDocumentFocusIn(e) {
    // Close when focusing out of the select
    const path = e.composedPath()
    if (this && !path.includes(this)) {
      this._active = false
    }
  }

  sanitize(value) {
    return value.replace('&amp;', '&')
  }

  get button() {
    return this.querySelector('[slot="button"]') ?? null
  }

  render() {
    const allOptions = this._getAllOptions()
    const currentIndex = allOptions.indexOf(this._currentOption)

    return html`
      <!-- @ts-ignore no-incompatible-type-binding -->
      <div class="container">
        <div
          id="${this.uuid}-label"
          class="${classMap({
            hidden: this._selected === '',
          })}"
        >
          <slot name="title"></slot>
        </div>
        <button
          @click=${this.toggle}
          type="button"
          class="${classMap({
            button: true,
            selected: this._selected !== '',
            active: this._active,
          })}"
          aria-expanded="${this._active}"
          aria-controls="${this.uuid}-list"
          aria-describedby="${this.uuid}-label"
        >
          <slot name="button"></slot>
          <slot name="icon"></slot>
        </button>
        <slot name="input"></slot>
        <ul
          id="${this.uuid}-list"
          class="${classMap({
            list: true,
            'is-active': this._active,
            above: this._showAbove,
          })}"
          @keyup="${this.toggle}"
          aria-hidden="${!this._active}"
          role="listbox"
          aria-activedescendant="${this.uuid}-list-${currentIndex}"
          ${ref(this.$list)}
        >
          ${this.options.map((option, idx) => {
            this.lang && (option.lang = option.value)
            return html`<li
              class="${classMap({
                option: true,
                'is-active': option.value === this._selected,
              })}"
              role="option"
              aria-selected="${option.label === this._selected}"
              id="${this.uuid}-list-${idx}"
            >
              <a
                @click="${(e) => this.selectValue(e, option.value)}"
                class="link"
                href="#"
                hreflang="${ifDefined(option.lang)}"
                lang="${ifDefined(option.lang)}"
              >
                ${this.sanitize(option.label)}
              </a>
            </li>`
          })}
        </ul>
      </div>
    `
  }

  /* prettier-ignore */
  static styles = css`
    :host .hidden {
      display: none;
    }

    :host() {
      outline: 0.2rem solid var(--hh-color-focus);
      outline-offset: var(--hh-focus-outline-offset);
    }

    *:focus-visible {
      outline: 0.2rem solid var(--hh-color-focus);
      outline-offset: var(--hh-focus-outline-offset);
    }

    /* Fallback - for browsers that don't support :focus-visible, a fallback is set for :focus */
    *:focus {
      outline: 0.2rem solid var(--hh-color-focus);
      outline-offset: var(--hh-focus-outline-offset);
    }

    /* Negate the fallback side-effect for browsers that support :focus-visible */
    *:focus:not(:focus-visible) {
      outline: 0;
      box-shadow: none;
    }

    .focus-inset:focus-visible {
      outline: 0.2rem solid var(--hh-color-focus);
      outline-offset: var(--hh-focus-outline-offset);
    }

    /* Fallback */
    .focus-inset:focus {
      outline: 0.2rem solid var(--hh-color-focus);
      outline-offset: var(--hh-focus-outline-offset);
    }

    .focus-inset:focus:not(:focus-visible) {
      outline: 0;
      box-shadow: none;
    }

    ::slotted([slot='title']) {
      position: absolute;
      margin-top: 5px;
      margin-bottom: 0;
      color: var(--hh-placeholder-color, currentColor);
      font-size: var(--hh-minified-font-size, 9px);
      margin-left: var(--hh-button-padding, 2em);
      pointer-events: none;
    }

    .container {
      position: relative;
    }

    .button {
      font: var(--hh-font);
      display: flex;
      width: var(--hh-width, 100%);
      height: var(--hh-height, 35px);
      color: var(--hh-placeholder-color, currentColor);
      background: transparent;
      padding: 0;
      border: var(--hh-border-width, 1px) solid currentColor;
      padding: var(--hh-button-padding, 2em);
      cursor: pointer;
    }

    .button.active {
      outline: 0.2rem solid var(--hh-color-focus);
      outline-offset: var(--hh-focus-outline-offset);
    }

    .button.selected {
      color: currentColor;
    }

    .button.selected ::slotted([slot='button']) {
      margin-top: 5px;
    }

    .button[aria-expanded='true'] ::slotted([slot='icon']) {
      transform: rotate(180deg);
    }

    ::slotted([slot='button']),
    ::slotted([slot='icon']) {
      display: flex;
      height: 100%;
      align-items: center;
    }

    ::slotted([slot='icon']) {
      position: absolute;
      top: 0;
      right: 0;
      margin-right: var(--hh-button-padding, 2em);
      transition: transform var(--hh-duration-default) ease;
    }

    .list {
      width: max-content;
      min-height: var(--hh-options-min-height, 8.2rem);
      max-height: var(--hh-options-max-height, 19rem);
      min-width: var(--hh-options-min-width, 12rem);
      max-width: var(--hh-options-max-width, 22rem);
      display: block;
      overflow-y: auto;
      box-sizing: border-box;
      position: absolute;
      background-color: var(--hh-background, white);
      border: var(--hh-border-width, 1px) solid currentColor;
      margin-top: -6px;
      margin-bottom: 4px;
      margin-left: var(--hh-options-width-offset, 0);
      padding-left: 0;
      z-index: 3;
      visibility: hidden;
      opacity: 0;
      transform: translateY(0);
    }

    .list.above {
      bottom: 100%;
    }

    .list.is-active {
      visibility: visible;
      opacity: 1;
      transform: translateY(5px);
    }

    .option {
      list-style: none;
      color: var(--hh-option-color, currentColor);
      border: var(--hh-option-border-width, 0) solid var(--hh-option-border-color);
    }

    .option:hover,
    .option:has(.link:focus) {
      color: var(--hh-option-hover-color, currentColor);
      background-color: var(--hh-option-hover-background, currentColor);
    }

    .option.is-active {
      color: var(--hh-option-active-color, currentColor);
      opacity: var(--hh-option-active-opacity, 1);
    }

    .link {
      display: block;
      text-decoration: none;
      padding: var(--hh-link-padding, 1em);
      color: inherit;
    }
  `
}

customElements.define('hh-custom-select', HHCustomSelect)
